/**
 * The host-neutral wire contract for one governed page composition (#2033).
 *
 * A page arranges portable dashboard-widget identities. It never carries a
 * route, component name, callback, SQL or URL. The server resolves every block
 * under the caller's RLS and this reader preserves that answer; it does not
 * infer availability in the browser.
 */
import { parseDescribedDashboard, type DescribedItem, type DescribedProvenance } from './dashboard.js'
import {
  pageDefinitionBlocks, parsePageDefinition,
  type PageDefinition, type PageDefinitionBlock,
} from './pageDefinition.js'
import { exactKeys, integerInRange, isRecord } from './documentShape.js'

const KEY = /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/u
const PAGE_KEY = /^(console|portal)\.[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/u
const STATES = [
  'ready', 'loading', 'empty', 'partial', 'offline', 'error', 'refused',
  'missing_install', 'entitlement_lapsed',
] as const

export type PageBlockState = typeof STATES[number]
export type PageHost = 'console' | 'portal'
export type PageSpan = { desktop: 1 | 2 | 3 | 4; tablet: 1 | 2 | 3 | 4; phone: 1 | 2 }

export interface PageWidgetBlock {
  id: string
  kind: 'widget'
  dashboard: string
  item: string
  span: PageSpan
  hidden: boolean
}

export interface PageCompositionSection {
  id: string
  label: string
  description: string | null
  columns: number | { desktop: number; tablet: number; phone: number }
  items: PageWidgetBlock[]
}

export interface LegacyPageCompositionDocument {
  schemaVersion: 1 | 2
  id: string
  revision: number
  surface: 'page'
  sections: PageCompositionSection[]
  unplaced: PageWidgetBlock[]
  dependencies: { kind: string; key: string; version: number | string | null }[]
}

export type PageCompositionDocument = LegacyPageCompositionDocument | PageDefinition

export interface DescribedPageBlock {
  id: string
  /** Absent on the legacy v1/v2 wire and therefore equivalent to widget. */
  kind?: PageDefinitionBlock['kind']
  section: string
  row?: string | null
  column?: string | null
  span: PageSpan
  state: PageBlockState
  reason: string | null
  /** Present for v3. V1/v2 widget blocks retain their legacy fields below. */
  reference?: PageDefinitionBlock | null
  dashboard: string
  item: string
  dashboardDescription: { id: string; logicalKey: string; name: string; revision: number } | null
  provenance: DescribedProvenance | null
  descriptor: DescribedItem | null
}

export interface DescribedPage {
  resolved: boolean
  state: 'ready' | 'empty' | 'error'
  host: PageHost | ''
  pageKey: string
  reason: string | null
  document: PageCompositionDocument | null
  provenance: { scope: string; persona: string | null; solutionKey: string | null; version: number } | null
  blocks: DescribedPageBlock[]
}

function parseSpan(value: unknown, schemaVersion: 1 | 2): PageSpan | null {
  if (!isRecord(value) || !exactKeys(value, ['desktop', 'tablet', 'phone'])
      || !integerInRange(value.desktop, 1, 4) || !integerInRange(value.tablet, 1, 4)
      || !integerInRange(value.phone, 1, schemaVersion === 1 ? 1 : 2)) return null
  return value as PageSpan
}

function parseBlock(value: unknown, schemaVersion: 1 | 2): PageWidgetBlock | null {
  if (!isRecord(value)
      || !exactKeys(value, ['id', 'kind', 'dashboard', 'item', 'span', 'hidden'])
      || value.kind !== 'widget' || typeof value.id !== 'string' || !KEY.test(value.id)
      || typeof value.dashboard !== 'string' || !KEY.test(value.dashboard)
      || typeof value.item !== 'string' || !KEY.test(value.item)
      || typeof value.hidden !== 'boolean') return null
  const span = parseSpan(value.span, schemaVersion)
  return span ? {
    id: value.id, kind: 'widget', dashboard: value.dashboard,
    item: value.item, span, hidden: value.hidden,
  } : null
}

/** Closed reader for the persisted page-specialised LayoutDocument. */
export function parsePageCompositionDocument(value: unknown): PageCompositionDocument | null {
  const v3 = parsePageDefinition(value)
  if (v3) return v3
  if (!isRecord(value)
      || !exactKeys(value, ['schemaVersion', 'id', 'revision', 'surface', 'sections', 'unplaced', 'dependencies'])
      || (value.schemaVersion !== 1 && value.schemaVersion !== 2)
      || value.surface !== 'page' || typeof value.id !== 'string' || !PAGE_KEY.test(value.id)
      || !integerInRange(value.revision, 1, Number.MAX_SAFE_INTEGER)
      || !Array.isArray(value.sections) || !Array.isArray(value.unplaced)
      || !Array.isArray(value.dependencies)) return null

  const schemaVersion = value.schemaVersion
  const ids = new Set<string>()
  const sections: PageCompositionSection[] = []
  for (const raw of value.sections) {
    if (!isRecord(raw) || !exactKeys(raw, ['id', 'label', 'description', 'columns', 'items'])
        || typeof raw.id !== 'string' || !KEY.test(raw.id) || ids.has(raw.id)
        || typeof raw.label !== 'string' || raw.label.trim() === ''
        || !(raw.description === null || typeof raw.description === 'string')
        || !Array.isArray(raw.items)) return null
    ids.add(raw.id)
    const columns = schemaVersion === 1
      ? integerInRange(raw.columns, 1, 4) ? raw.columns as number : null
      : parseSpan(raw.columns, 2)
    if (columns === null) return null
    const items: PageWidgetBlock[] = []
    for (const candidate of raw.items) {
      const block = parseBlock(candidate, schemaVersion)
      if (!block || ids.has(block.id)) return null
      ids.add(block.id)
      items.push(block)
    }
    sections.push({ id: raw.id, label: raw.label, description: raw.description, columns, items })
  }
  const unplaced: PageWidgetBlock[] = []
  for (const candidate of value.unplaced) {
    const block = parseBlock(candidate, schemaVersion)
    if (!block || ids.has(block.id)) return null
    ids.add(block.id)
    unplaced.push(block)
  }
  const dependencies: LegacyPageCompositionDocument['dependencies'] = []
  for (const raw of value.dependencies) {
    if (!isRecord(raw) || !exactKeys(raw, ['kind', 'key', 'version'])
        || typeof raw.kind !== 'string' || typeof raw.key !== 'string' || !KEY.test(raw.key)
        || !(raw.version === null || typeof raw.version === 'string' || typeof raw.version === 'number')) return null
    dependencies.push({ kind: raw.kind, key: raw.key, version: raw.version })
  }
  return {
    schemaVersion, id: value.id, revision: value.revision as number, surface: 'page',
    sections, unplaced, dependencies,
  }
}

/** Defensive one-reader boundary for `core.describe_page_experience`. */
export function parseDescribedPage(value: unknown): DescribedPage {
  const host = isRecord(value) && (value.host === 'console' || value.host === 'portal')
    ? value.host : ''
  const pageKey = isRecord(value) && typeof value.pageKey === 'string' ? value.pageKey : ''
  const reason = isRecord(value) && typeof value.reason === 'string' ? value.reason : null
  /* FAIL CLOSED unless the server said EMPTY in as many words (#1188/#2033,
     ROOT 68189 item 1).
     This used to collapse three different wires into `empty`, and both hosts
     read `empty` as permission to render their native screen:
       · `{resolved:false, state:'empty', reason:'No page composition is
         installed.'}` — the genuine case, and the only one a host may fall
         back from;
       · a REFUSAL, which the door explains in `reason` — the genuine-empty
         branch also carries a reason, so a reason cannot be the discriminator;
       · a wire that is not an object at all — null, a string, an array — which
         is the case where nothing is known and native UI is the worst answer.
     So the SERVER's own `state` decides, and anything else is an error. */
  if (!isRecord(value)) {
    return {
      resolved: false, state: 'error', host, pageKey,
      reason: 'The page composition could not be read safely.',
      document: null, provenance: null, blocks: [],
    }
  }
  if (value.resolved !== true) {
    const empty = value.state === 'empty'
    return {
      resolved: false, state: empty ? 'empty' : 'error', host, pageKey,
      reason: reason ?? (empty ? null : 'The page composition could not be read safely.'),
      document: null, provenance: null, blocks: [],
    }
  }
  const document = parsePageCompositionDocument(value.document)
  if (!document || document.id !== pageKey || !Array.isArray(value.blocks)) {
    return {
      resolved: false, state: 'error', host, pageKey,
      reason: 'The page composition could not be read safely.', document: null,
      provenance: null, blocks: [],
    }
  }
  const blocks: DescribedPageBlock[] = []
  if (document.schemaVersion === 3) {
    const described = new Map(value.blocks.flatMap(raw =>
      isRecord(raw) && exactKeys(raw, [
        'id', 'kind', 'section', 'row', 'column', 'span', 'reference', 'state', 'reason',
        'dashboardDescription', 'provenance', 'descriptor',
      ]) && typeof raw.id === 'string' ? [[raw.id, raw] as const] : []))
    for (const located of pageDefinitionBlocks(document)) {
      const raw = described.get(located.block.id)
      const span = document.sections
        .find(section => section.id === located.section)?.rows
        .find(row => row.id === located.row)?.columns
        .find(column => column.id === located.column)?.span
      if (!span) continue
      const rawMatches = !!raw
        && raw.kind === located.block.kind && raw.section === located.section
        && raw.row === located.row && raw.column === located.column
        && isRecord(raw.span)
        && raw.span.desktop === span.desktop && raw.span.tablet === span.tablet
        && raw.span.phone === span.phone
      const state = rawMatches && STATES.includes(raw!.state as PageBlockState)
        ? raw.state as PageBlockState
        : located.block.kind === 'widget' || located.block.when ? 'error' : 'ready'
      const descriptor = located.block.kind === 'widget' && rawMatches && isRecord(raw!.descriptor)
        ? parseDescribedDashboard({
            resolved: true, host, placement: 'page', items: [raw!.descriptor],
          }).dashboard.items?.[0] ?? null
        : null
      blocks.push({
        id: located.block.id, kind: located.block.kind, section: located.section,
        row: located.row, column: located.column, span,
        state, reason: rawMatches && typeof raw!.reason === 'string' ? raw!.reason
          : state === 'error' ? located.block.when
            ? 'The conditional block has no record-bound server verdict.'
            : 'The governed block was not described by the server.' : null,
        reference: located.block,
        dashboard: located.block.kind === 'widget' || located.block.kind === 'dashboard'
          ? located.block.dashboard : '',
        item: located.block.kind === 'widget' ? located.block.item : '',
        dashboardDescription: rawMatches && isRecord(raw!.dashboardDescription)
          ? raw!.dashboardDescription as DescribedPageBlock['dashboardDescription'] : null,
        provenance: rawMatches && isRecord(raw!.provenance)
          ? raw!.provenance as unknown as DescribedProvenance : null,
        descriptor,
      })
    }
  } else {
    const declared = new Map(document.sections.flatMap(section =>
      section.items.map(block => [block.id, { block, section: section.id }] as const)))
    for (const raw of value.blocks) {
      if (!isRecord(raw) || !exactKeys(raw, [
        'id', 'section', 'dashboard', 'item', 'span', 'state', 'reason',
        'dashboardDescription', 'provenance', 'descriptor',
      ]) || typeof raw.id !== 'string' || typeof raw.section !== 'string'
        || typeof raw.dashboard !== 'string' || typeof raw.item !== 'string'
        || !STATES.includes(raw.state as PageBlockState)) continue
      const source = declared.get(raw.id)
      const span = parseSpan(raw.span, document.schemaVersion)
      if (!source || source.section !== raw.section || source.block.dashboard !== raw.dashboard
          || source.block.item !== raw.item || !span) continue
      const descriptor = isRecord(raw.descriptor)
        ? parseDescribedDashboard({
            resolved: true, host, placement: 'page', items: [raw.descriptor],
          }).dashboard.items?.[0] ?? null
        : null
      blocks.push({
        id: raw.id, kind: 'widget', section: raw.section, row: null, column: null,
        dashboard: raw.dashboard, item: raw.item, span, reference: null,
        state: raw.state as PageBlockState,
        reason: typeof raw.reason === 'string' ? raw.reason : null,
        dashboardDescription: isRecord(raw.dashboardDescription)
          ? raw.dashboardDescription as DescribedPageBlock['dashboardDescription'] : null,
        provenance: isRecord(raw.provenance) ? raw.provenance as unknown as DescribedProvenance : null,
        descriptor,
      })
    }
  }
  return {
    resolved: true, state: 'ready', host, pageKey, reason: null, document,
    provenance: isRecord(value.provenance)
      ? value.provenance as unknown as DescribedPage['provenance'] : null,
    blocks,
  }
}
